<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Configure mappings</h1>

                    <a :href="`/#/admin/integrations/pipelines/details/${tid}/${id}`" type="button" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        <span>Back</span>
                    </a>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div class="row">
                <div class="col-5 text-right" :class="{ 'order-1': direction==1, 'order-3': direction==2 }">
                    <h5>Connector</h5>
                </div>

                <div class="col-2 order-2 text-center">
                    <i v-if="direction==1" class="fa fa-arrow-circle-right fa-2x" title="From connector to dataset"></i>
                    <i v-if="direction==2" class="fa fa-arrow-circle-right fa-2x" title="From dataset to connector"></i>
                </div>

                <div class="col-5" :class="{ 'order-1': direction==2, 'order-3': direction==1 }">
                    <h5>Dataset</h5>
                </div>
            </div>

            <div class="row mb-2">
                <div class="col-5" :class="{ 'order-1': direction==1, 'order-3': direction==2 }">
                    <select v-model.trim="nameForConnector" class="form-control">
                        <option v-for="p in availablePropertyNames">{{p}}</option>
                    </select>
                </div>

                <div class="col-2 order-2 text-center">
                    <button @click="handleAddPropertyNameMappingCommand" class="btn bt-sm btn-secondary">Add</button>
                </div>

                <div class="col-5" :class="{ 'order-1': direction==2, 'order-3': direction==1 }">
                    <input v-model.trim="nameForDataset" type="text" class="form-control">
                </div>
            </div>

            <div v-for="m in mappings" class="row mb-1">
                <div class="col-5 text-right" :class="{ 'order-1': direction==1, 'order-3': direction==2 }">
                    <strong>{{m.NameForConnector}}</strong>
                </div>

                <div class="col-2 order-2 text-center">
                    <a @click="handleRemovePropertyNameMappingCommandV1(m)" role="button">
                        <i class="fa fa-remove fg-color-red" aria-hidden="true"></i>
                    </a>
                </div>

                <div class="col-5" :class="{ 'order-1': direction==2, 'order-3': direction==1 }">
                    <strong>{{m.NameForDataset}}</strong>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
    import { useId, useOrganizationUnitId } from '@common/composables'
    import { useAdminConfigurePipelineMappingsPage } from '@Integrations/Pipelines/pages-core'

    document.title = "Configure mappings"

    const tid = useOrganizationUnitId()
    const id = useId()

    const connectorId = ref()

    const idPropertyNameForConnector = ref()
    const idPropertyNameForDataset = ref()

    const idPropertyNamesForConnector = ref([])
    const idPropertyNamesForDataset = ref([])

    const nameForConnector = ref()
    const nameForDataset = ref()

    const {
        useCurrentUserQuery,
        usePipelineDetailsQuery,
        useConnectorPropertyNamesQuery,
        useAddPropertyNameMappingCommand,
        useRemovePropertyNameMappingCommand
    } = useAdminConfigurePipelineMappingsPage()

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)

    const { runPipelineDetailsQueryV1, modelPipelineDetailsQueryV1 } = usePipelineDetailsQuery(id)
    const direction = computed(() => modelPipelineDetailsQueryV1.value?.Direction)
    const properties = computed<string>(() => modelPipelineDetailsQueryV1.value?.Properties ?? [])
    const mappings = computed(() => modelPipelineDetailsQueryV1.value?.Mappings || [])

    const { runConnectorPropertyNamesQueryV1, modelConnectorPropertyNamesQueryV1 } = useConnectorPropertyNamesQuery(connectorId)
    const propertyNames = computed(() => modelConnectorPropertyNamesQueryV1.value?.PropertyNames || [])

    const { invokeAddPropertyNameMappingCommandV1, succeedAddPropertyNameMappingCommandV1 } = useAddPropertyNameMappingCommand(id, tid, userId, nameForConnector, nameForDataset)
    const { invokeRemovePropertyNameMappingCommandV1, succeedRemovePropertyNameMappingCommandV1 } = useRemovePropertyNameMappingCommand(id, tid, userId)

    const availablePropertyNames = computed(() => properties.value.length > 0 ? properties.value : propertyNames.value)

    function handleAddPropertyNameMappingCommand() {
        invokeAddPropertyNameMappingCommandV1()
    }

    function handleRemovePropertyNameMappingCommandV1(mapping: any) {
        invokeRemovePropertyNameMappingCommandV1({
            nameForConnector: mapping.NameForConnector,
            nameForDataset: mapping.NameForDataset,
        })
    }

    watch(modelPipelineDetailsQueryV1, () => {
        connectorId.value = modelPipelineDetailsQueryV1.value.ConnectorId
    })

    watch(connectorId, () => runConnectorPropertyNamesQueryV1())

    watch(succeedAddPropertyNameMappingCommandV1, () => runPipelineDetailsQueryV1())
    watch(succeedRemovePropertyNameMappingCommandV1, () => runPipelineDetailsQueryV1())

    watch(succeedAddPropertyNameMappingCommandV1, () => {
        nameForConnector.value = ''
        nameForDataset.value = ''
    })

    onMounted(() => {
        runCurrentUserQueryV1()
        runPipelineDetailsQueryV1()
    })
</script>